import type { MapSource } from '../map';

/**
 * Zombie Nights' arenas (ADR-0164, ADR-0165), under every zombies map (ADR-0176): the Barrow, where the party meets the
 * Buried King after night `KING.night`, and under it the Fallow, where it meets the Yoke after night `YOKE.night`. Their
 * rows are 60 cells wide, as every zombies map is. Each a platform (`-`) over the abyss (`^`), nothing to hide behind.
 */
export const ZOMBIE_ARENAS: NonNullable<MapSource['arenas']> = [
  // The Barrow (ADR-0164): a slab of stone hanging over a sea of fire, the King leaning over its north edge (the
  // eleven rows north of it are his).
  {
    stage: { side: 2, north: 11, south: 3 },
    rows: `
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^--------------------------^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
      `,
  },
  // The Fallow (ADR-0165): a ploughed field on a slab of its own over the same sea, wide and deep enough to run on.
  {
    stage: { side: 3, north: 4, south: 4 },
    rows: `
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^----------------------------^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
      `,
  },
];

/**
 * Moonlit Farm, the Zombie Nights map (ADR-0098): the Greenhouse (the blue base, `B`) in a small yard of cobble (bare dirt
 * to the eye, ADR-0206 amended) with five spawns round it (its apple crates are a run's, hashed round the field: `fruitSpots`), inside one ring of hedges broken by the eight dirt paths that run out to
 * the graveyard gates round the edge (`Z`: where the night's zombies rise). The ring's gaps are the chokepoints; an
 * orchard, two ponds and a rock garden shape the field between it and the gates; everything else is open grass to build
 * on. No fog: the night is the client's (the whole map goes dark but the yard and the lanes' inner halves).
 *
 * Under it, cut off from it, Zombie Nights' arenas (`ZOMBIE_ARENAS`). Tonight's side turns round the farm (`nightGates`):
 * the expert's map (ADR-0176), every lane's end at the plaza.
 */
export const ZOMBIE_FARM: MapSource = {
  id: 'zombie-farm',
  name: 'Eight Feet Under',
  // Drawn in the village packs, as the battle royale's Village is (ADR-0206).
  style: 'village',
  rows: `
    ############################################################
    #.......................,,,..:::....................,,,....#
    #......................,,,,,:::::....................,.....#
    #...:::....,............,,,.::Z::.....................:::..#
    #..:::::..,,,............,..:::::....................:::::.#
    #..::Z::...,.................:::.....................::Z::.#
    #..:::::......................::.....................:::::.#
    #...:::::.....................::.....................::::..#
    #......:::...........,T.......::....................:::....#
    #.......:::.........,,,.......::...................:::,....#
    #........:::.........,........::..................:::,.....#
    #.........:::.................::.........~~~.....:::.......#
    #........o.::T..T..T..........::........~~~~~...:::........#
    #...........:::...............::........~~~~~..:::.........#
    #............:::...........o..::........~~~~~.:::..........#
    #............T::T..T..........::.........~~~.:::...........#
    #...........T..:::............::............:::............#
    #............T..:::...........::...........:::.............#
    #..........T.T..T::T......##..::##........:::..T...........#
    #.................:::...###...::.###.....:::.T.............#
    #..................:::.##.....::...##...:::................#
    #.................o.:::.......::.......:::.................#
    #.................,..:::......::......:::..........T.......#
    #................,,,#.:::.....::.....::#...................#
    #...............,,,##..:::....::....:::##......,...........#
    #................,,#....:::...::,..:::..#.....,,,..........#
    #.................##T....:::..::..:::...##....,,,,.........#
    #.................#.......:::.::.:::.....#....,,,..........#
    #.:::......................::b==b::............,.......:::.#
    #:::::......................======............o.......:::::#
    #::Z::::::::::::::::::::::::==BB==::::::::::::::::::::::Z::#
    #:::::::::::::::::::::::::::==BB==:::::::::::::::::::::::::#
    #.:::............,#,........======.,,....#.............:::.#
    #.................##.......::bb=b::,....##.................#
    #..................#......:::.::.:::....#..................#
    #..................##....:::.,::..:::.,##..................#
    #...................#...:::..,::...:::,#...................#
    #.................,....:::..,,::,...:::.,..................#
    #.......T........,,,..:::....,::.....:::,,.................#
    #...............,,,,,::##.....::...##.:::,o................#
    #................,,,:::.###...::.###...:::.................#
    #.................,:::....##..::##......:::................#
    #................~:::.........::.........:::.o.............#
    #......,,.......~:::..........::..........:::.o............#
    #.....,,,,.....~:::.~.........::...........:::.T...........#
    #....,,,,,.....:::.~..........::..........o.::::...........#
    #...,,,,,.....:::.~...........::.o.,,,.....o.:::...........#
    #....,.,.o...:::..............::..,,,,,....oT.:::..........#
    #...........:::...............::...,,,.........:::.o.......#
    #..........:::................::....,...........:::........#
    #.........:::.................::.................:::.......#
    #........:::...,..............::......T...........:::......#
    #.......:::...,,,.............::...................:::.....#
    #...::::::.....,..............::....................:::::..#
    #..::::::....................:::........,............:::::.#
    #..::Z::....................:::::......,,,...........::Z::.#
    #..:::::....................::Z::.....,,,,,..........:::::.#
    #...:::.....................:::::......,,,............:::..#
    #............................:::........,..................#
    ############################################################
  `,
  arenas: ZOMBIE_ARENAS,
  // Chomper (ADR-0180, ADR-0186): in from the edge, careening round the whole yard (all four quarters, out towards the
  // corners, through the plaza, crossing its own way) as if it had lost its mind, out again the way it came.
  chomper: {
    route: [
      [62, 28],
      [46, 28],
      [46, 20],
      [52, 20],
      [52, 9],
      [20, 9],
      [20, 4],
      [10, 4],
      [10, 24],
      [13, 24],
      [13, 37],
      [6, 37],
      [6, 49],
      [26, 49],
      [26, 46],
      [23, 46],
      [23, 53],
      [48, 53],
      [48, 22],
      [33, 22],
      [33, 37],
      [27, 37],
      [27, 28],
      [62, 28],
    ],
  },
  // Half as much again at every dawn (ADR-0206 amended): with the Greenhouse where its eight lanes cross, its towers
  // stand half a cell further off it and do less: the harness's plaza defenders fell from 13 nights (median) to 9-11 on
  // the purse every map pays, and hold 13 again on this.
  dawnScale: 1.5,
};
